import { UiDiv } from '@webfox-sc/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../app/appHooks';
import { useCheckitemgrouptype } from '../../../app/entityHooks/useCheckitemgrouptype';
import {
  createCheckitemgrouptype,
  deleteCheckitemgrouptype,
  updateCheckitemgrouptype,
} from '../../../app/slices/checkitemgrouptypesSlice';
import IconCheck from '../../../assets/icons-v2/IconCheck';
import IconClose from '../../../assets/icons-v2/IconClose';
import IconDelete from '../../../assets/icons-v2/IconDelete';
import IconRefresh from '../../../assets/icons-v2/IconRefresh';
import Button from '../../atoms-v2/Button';
import FormElementLabel from '../../atoms-v2/FormElementLabel';
import TextInput from '../../atoms-v2/TextInput';

interface BaseDataQMCheckitemgrouptypeFormProps {
  checkitemgrouptypeId?: number | undefined;
  onSubmit?: React.FormEventHandler<HTMLFormElement | HTMLButtonElement>;
  onClickCancel?: React.MouseEventHandler<HTMLButtonElement>;
}

const BaseDataQMCheckitemgrouptypeForm: React.FC<BaseDataQMCheckitemgrouptypeFormProps> = ({
  checkitemgrouptypeId,
  onSubmit,
  onClickCancel,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formMode = checkitemgrouptypeId ? 'update' : 'create';

  const { checkitemgrouptype } = useCheckitemgrouptype(checkitemgrouptypeId);
  const [checkitemgrouptypeData, setCheckitemgrouptypeData] = useState<CheckitemgrouptypeUpdateData>({});

  useEffect(() => {
    setCheckitemgrouptypeData({
      name: checkitemgrouptype?.name,
      disabled: checkitemgrouptype ? checkitemgrouptype?.disabled : false,
      canDelete: checkitemgrouptype ? checkitemgrouptype?.canDelete : false,
    });
  }, [checkitemgrouptype]);

  const isFormDataValid = () => {
    const { name } = checkitemgrouptypeData;
    return !!name;
  };

  const handleChangeValue = (key: string, value: any) => {
    const checkitemgrouptypeDataUpdated: CheckitemgrouptypeUpdateData = { ...checkitemgrouptypeData };
    checkitemgrouptypeDataUpdated[key] = value;
    setCheckitemgrouptypeData(checkitemgrouptypeDataUpdated);
  };

  const handleOnSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (formMode === 'create') {
      await dispatch(createCheckitemgrouptype(checkitemgrouptypeData));
    } else if (checkitemgrouptypeId) {
      await dispatch(updateCheckitemgrouptype(checkitemgrouptypeId, checkitemgrouptypeData));
    }
    if (onSubmit) {
      onSubmit(e);
    }
  };

  const handleOnClickDisable: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (checkitemgrouptype) {
      if (window.confirm(t('checkitemGroup.prompts.delete', { name: checkitemgrouptype.name }) || undefined)) {
        if (checkitemgrouptype) {
          await dispatch(
            updateCheckitemgrouptype(checkitemgrouptype.id, { ...checkitemgrouptypeData, disabled: true })
          );
        }
        if (onSubmit) {
          onSubmit(e);
        }
      }
    }
  };

  const handleOnClickDelete: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (checkitemgrouptype) {
      if (window.confirm(t('checkitemGroup.prompts.deleteFinally', { name: checkitemgrouptype.name }) || undefined)) {
        if (checkitemgrouptype) {
          await dispatch(deleteCheckitemgrouptype(checkitemgrouptype.id));
        }
        if (onSubmit) {
          onSubmit(e);
        }
      }
    }
  };

  const handleOnClickRestore: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (checkitemgrouptype) {
      await dispatch(updateCheckitemgrouptype(checkitemgrouptype.id, { ...checkitemgrouptypeData, disabled: false }));
    }
    if (onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <form onSubmit={handleOnSubmit}>
      <UiDiv>
        <FormElementLabel>{t('common.fields.name')} *</FormElementLabel>
        <TextInput value={checkitemgrouptypeData.name} onChange={(e) => handleChangeValue('name', e.target.value)} />
      </UiDiv>
      <UiDiv display="flex" columnGap="xs" justifyContent="space-between" paddingTop="s">
        <UiDiv display="flex" columnGap="xs">
          <Button type="submit" icon={IconCheck} disabled={!isFormDataValid()}>
            {formMode === 'create' ? 'Erstellen' : 'Übernehmen'}
          </Button>
          <Button variant="secondary" icon={IconClose} onClick={onClickCancel}>
            {t('common.actions.cancel')}
          </Button>
        </UiDiv>
        {formMode === 'update' && (
          <>
            {checkitemgrouptypeData.disabled ? (
              <UiDiv display="flex" columnGap="xs">
                <Button icon={IconRefresh} onClick={handleOnClickRestore}>
                  {t('common.actions.restore')}
                </Button>
                <Button
                  icon={IconDelete}
                  variant="secondary"
                  onClick={handleOnClickDelete}
                  disabled={!checkitemgrouptypeData.canDelete}
                >
                  {t('common.actions.deleteFinally')}
                </Button>
              </UiDiv>
            ) : (
              <Button icon={IconDelete} onClick={handleOnClickDisable}>
                {t('common.actions.delete')}
              </Button>
            )}
          </>
        )}
      </UiDiv>
    </form>
  );
};

export default BaseDataQMCheckitemgrouptypeForm;
